import clsx from 'clsx'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'

import { Icon } from '../../Icon'
import Button from './Button'
import Label from './Label'
import Paragraph from './Paragraph'

type Props = {
    title?: string
    subtitle?: string
    buttonText?: string
    intent: 'info' | 'warning' | 'danger' | 'success'
    onClick?: () => void
    onClose?: VoidFunction
}

function InfoBanner({ title, subtitle, buttonText, intent, onClick, onClose }: Props) {
    const intentClasses = {
        info: 'bg-info-100 border-info-200 ',
        warning: 'bg-warning-100 border-warning-200 ',
        danger: 'bg-danger-100 border-danger-200 ',
        success: 'bg-success-100 border-success-200',
    }

    const buttonIntentClasses = {
        info: 'text-info-400',
        warning: 'text-warning-600',
        danger: 'text-danger-400',
        success: 'text-success-400',
    }

    const iconIntentClasses = {
        info: 'text-info-400',
        warning: 'text-warning-600',
        danger: 'text-danger-400',
        success: 'text-success-400',
    }

    const shouldAlignItemsCenter =
        (!Boolean(subtitle) && Boolean(title)) || (Boolean(subtitle) && !Boolean(title))

    const titleAndSubtitle = useMemo(() => {
        if (Boolean(title) && Boolean(subtitle)) {
            return (
                <div className="flex grow flex-col gap-y-1">
                    <Label size="md" className="items-center text-gray-700">
                        {title}
                    </Label>
                    <Paragraph size="md" className="text-gray-600">
                        {subtitle}
                    </Paragraph>
                </div>
            )
        }

        if (Boolean(title)) {
            return (
                <Label size="md" className="grow items-center text-gray-700">
                    {title}
                </Label>
            )
        }

        if (Boolean(subtitle)) {
            return (
                <Paragraph size="md" className="grow text-gray-600">
                    {subtitle}
                </Paragraph>
            )
        }

        return null
    }, [title, subtitle])

    return (
        <section
            data-testid="info-banner"
            className={`flex items-center justify-between rounded-lg border-2 p-4 ${intentClasses[intent]}`}
            onClick={onClick}
        >
            <div className={clsx('flex w-full gap-4', { 'items-center': shouldAlignItemsCenter })}>
                <Icon
                    icon={'exclamation.large'}
                    className={`${iconIntentClasses[intent]} shrink-0`}
                    shouldInheritColor={true}
                />

                {titleAndSubtitle}

                <div className="flex items-center gap-4">
                    {Boolean(buttonText) && (
                        <Button
                            size="small"
                            className={` ${buttonIntentClasses[intent]} whitespace-nowrap`}
                        >
                            {buttonText}
                        </Button>
                    )}

                    {!!onClose && (
                        <Button
                            size="small"
                            shape="square"
                            intent="tertiary_deprecated"
                            className="whitespace-nowrap"
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation()
                                e.preventDefault()
                                onClose()
                            }}
                        >
                            <Icon
                                icon="close.medium"
                                className="text-gray-500"
                                shouldInheritColor
                            />
                        </Button>
                    )}
                </div>
            </div>
        </section>
    )
}

export default InfoBanner
